import { StyleManager } from "./StyleManager.js";
import { LifecycleManager } from "./LifecycleManager.js";
import { isElementBuilder, isElementModel } from "./types.js";
class ElementBuilder {
  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================
  /**
   * Create a new ElementBuilder
   *
   * @overload
   * @param tag - HTML tag name to create (e.g., 'div', 'span', 'a')
   * @param options - Optional builder configuration
   *
   * @overload
   * @param element - Existing HTMLElement to wrap
   * @param options - Optional builder configuration
   *
   * @overload
   * @param options - Optional builder configuration (creates a div)
   */
  constructor(elementOrTagOrOptions, options = {}) {
    this.isBuilt = false;
    this.customEvents = {};
    if (!elementOrTagOrOptions) {
      this.element = document.createElement("div");
    } else if (typeof elementOrTagOrOptions === "string") {
      this.element = document.createElement(
        elementOrTagOrOptions
      );
    } else if (elementOrTagOrOptions instanceof HTMLElement) {
      this.element = elementOrTagOrOptions;
    } else {
      this.element = document.createElement("div");
      options = elementOrTagOrOptions;
    }
    this.classNames = /* @__PURE__ */ new Set();
    this.styles = new StyleManager();
    this.attributes = /* @__PURE__ */ new Map();
    this.children = [];
    this.eventListeners = /* @__PURE__ */ new Map();
    this.modifiers = [];
    this.lifecycle = new LifecycleManager();
    this.options = options;
    if (options.className) {
      this.withClassName(...options.className);
    }
    if (options.attributes) {
      this.withAttributes(options.attributes);
    }
    if (options.text) {
      this.withText(options.text);
    }
    if (options.html) {
      this.withHTML(options.html);
    }
    if (options.children) {
      this.withChildren(...options.children);
    }
    if (options.styles) {
      this.withStyles(options.styles);
    }
    if (options.data) {
      this.withData(options.data);
    }
    if (options.aria) {
      this.withAria(options.aria);
    }
  }
  // ============================================================================
  // CLASS NAME METHODS
  // ============================================================================
  /**
   * Add one or more CSS class names to the element
   * @param names - Class names to add
   * @returns This builder for chaining
   */
  withClassName(...names) {
    this.assertNotBuilt();
    names.forEach((name) => {
      if (name && name.trim()) {
        this.classNames.add(name.trim());
      }
    });
    return this;
  }
  // ============================================================================
  // STYLE METHODS
  // ============================================================================
  /**
   * Add custom inline styles (JSS object, ElementStyles, or StyleDefinition)
   *
   * Custom styles have higher CSS cascade priority (outputted LAST in CSS),
   * meaning they override preset styles from .styled() for conflicting properties.
   *
   * Note: Requires a className to be set via .withClassName() or .styled()
   * If no className is present, styles will be skipped with a warning
   *
   * @param styles - Style definition or ElementStyles object
   * @param priority - Optional priority for CSS cascade order (higher = outputted last, default: 2)
   * @returns This builder for chaining
   */
  withStyles(styles, priority = 2) {
    this.assertNotBuilt();
    const className = this.getCurrentClassName();
    if (!className) {
      console.warn(
        "ElementBuilder.withStyles(): No className found. Use .withClassName() or .styled() before adding styles. Skipping style application."
      );
      return this;
    }
    this.styles.add(styles, className, priority);
    return this;
  }
  /**
   * Conditionally add custom inline styles
   * @param condition - Whether to add the styles
   * @param styles - Styles to add if condition is true
   * @param priority - Optional priority (higher = outputted last in CSS, default: 2)
   * @returns This builder for chaining
   */
  withStylesIf(condition, styles, priority = 2) {
    if (condition) {
      this.withStyles(styles, priority);
    }
    return this;
  }
  /**
   * Apply design system preset styles from the styles library
   *
   * This is a convenience method that:
   * 1. Extracts the className from the style object
   * 2. Applies the complete style object as base/default styles
   *
   * Preset styles from .styled() have lower CSS cascade priority (outputted FIRST),
   * meaning custom .withStyles() will override them for conflicting properties.
   *
   * @param styleObject - JSS StyleDefinition from @universityofmaryland/web-styles-library
   * @param priority - Optional priority for CSS cascade order (lower = outputted first, default: 1)
   * @returns This builder for chaining
   *
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   *
   * const headline = new ElementBuilder(headlineElement)
   *   .styled(Styles.typography.sans.larger)    // Priority 1 - base
   *   .withStyles({ element: { color: 'red' }}) // Priority 2 - overrides
   *   .build();
   *
   * // CSS output (cascade order):
   * // .class { ...preset styles... }
   * // .class { color: red; } // custom overrides
   * ```
   */
  styled(styleObject, priority = 1) {
    this.assertNotBuilt();
    if (styleObject && typeof styleObject === "object" && "className" in styleObject) {
      const className = styleObject.className;
      if (typeof className === "string") {
        this.withClassName(className);
      } else if (Array.isArray(className)) {
        this.withClassName(...className);
      }
    }
    this.withStyles(styleObject, priority);
    return this;
  }
  // ============================================================================
  // ATTRIBUTE METHODS
  // ============================================================================
  /**
   * Add HTML attributes
   * @param attrs - Object of attribute key-value pairs
   * @returns This builder for chaining
   */
  withAttributes(attrs) {
    this.assertNotBuilt();
    Object.entries(attrs).forEach(([key, value]) => {
      if (value !== void 0 && value !== null) {
        this.attributes.set(key, String(value));
      }
    });
    return this;
  }
  /**
   * Add a single attribute
   * @param key - Attribute name
   * @param value - Attribute value
   * @returns This builder for chaining
   */
  withAttribute(key, value) {
    return this.withAttributes({ [key]: value });
  }
  /**
   * Set ARIA attributes for accessibility
   * @param attrs - ARIA attributes (with or without 'aria-' prefix)
   * @returns This builder for chaining
   */
  withAria(attrs) {
    const ariaAttrs = {};
    Object.entries(attrs).forEach(([key, value]) => {
      const attrKey = key.startsWith("aria-") ? key : `aria-${key}`;
      ariaAttrs[attrKey] = String(value);
    });
    return this.withAttributes(ariaAttrs);
  }
  /**
   * Set data attributes
   * @param attrs - Data attributes (with or without 'data-' prefix)
   * @returns This builder for chaining
   */
  withData(attrs) {
    const dataAttrs = {};
    Object.entries(attrs).forEach(([key, value]) => {
      const attrKey = key.startsWith("data-") ? key : `data-${key}`;
      dataAttrs[attrKey] = String(value);
    });
    return this.withAttributes(dataAttrs);
  }
  /**
   * Set role attribute for ARIA semantics
   * @param role - ARIA role (button, navigation, main, etc.)
   * @returns This builder for chaining
   */
  withRole(role) {
    return this.withAttribute("role", role);
  }
  // ============================================================================
  // CONTENT METHODS
  // ============================================================================
  /**
   * Set text content (replaces existing text)
   * @param text - Text content to set
   * @returns This builder for chaining
   */
  withText(text) {
    this.assertNotBuilt();
    this.modifiers.push((el) => {
      el.textContent = text;
    });
    return this;
  }
  /**
   * Set inner HTML (replaces existing content)
   * WARNING: Be careful with XSS - sanitize user input!
   * @param html - HTML string to set
   * @returns This builder for chaining
   */
  withHTML(html) {
    this.assertNotBuilt();
    this.modifiers.push((el) => {
      el.innerHTML = html;
    });
    return this;
  }
  // ============================================================================
  // CHILD METHODS
  // ============================================================================
  /**
   * Add a child element
   * @param child - Child to add (Builder, HTMLElement, or string)
   * @returns This builder for chaining
   */
  withChild(child) {
    this.assertNotBuilt();
    if (child !== null && child !== void 0) {
      this.children.push(child);
    }
    return this;
  }
  /**
   * Add multiple children
   * Supports ElementBuilder, ElementModel, HTMLElement, or string children
   * ElementModel children will have their styles automatically merged
   * @param children - Children to add
   * @returns This builder for chaining
   */
  withChildren(...children) {
    children.forEach((child) => this.withChild(child));
    return this;
  }
  /**
   * Conditionally add a child
   * @param condition - Whether to add the child
   * @param child - Child to add if condition is true
   * @returns This builder for chaining
   */
  withChildIf(condition, child) {
    if (condition) {
      const resolvedChild = typeof child === "function" ? child() : child;
      this.withChild(resolvedChild);
    }
    return this;
  }
  /**
   * Add children from an array (useful for mapping)
   * @param items - Array of items
   * @param mapper - Function to map each item to a child
   * @returns This builder for chaining
   */
  withChildrenFrom(items, mapper) {
    items.forEach((item, index) => {
      this.withChild(mapper(item, index));
    });
    return this;
  }
  // ============================================================================
  // EVENT METHODS
  // ============================================================================
  /**
   * Add an event listener
   * @param event - Event name (e.g., 'click', 'mouseover')
   * @param handler - Event handler function
   * @param options - Event listener options
   * @returns This builder for chaining
   */
  on(event, handler, options) {
    this.assertNotBuilt();
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push({ handler, options });
    this.lifecycle.trackListener(event, handler, options);
    return this;
  }
  /**
   * Add custom events/methods to the ElementModel
   * These will be available on the returned ElementModel.events object
   *
   * @param events - Object containing custom event handlers or methods
   * @returns This builder for chaining
   *
   * @example
   * ```typescript
   * const video = new ElementBuilder('div')
   *   .withEvents({
   *     play: () => videoElement.play(),
   *     pause: () => videoElement.pause(),
   *     seek: (time) => videoElement.currentTime = time
   *   })
   *   .build();
   *
   * // Use the events
   * video.events.play();
   * video.events.pause();
   * video.events.seek(30);
   * ```
   */
  withEvents(events) {
    this.assertNotBuilt();
    const domEvents = [
      "click",
      "dblclick",
      "mousedown",
      "mouseup",
      "mouseover",
      "mouseout",
      "mouseenter",
      "mouseleave",
      "keydown",
      "keyup",
      "keypress",
      "focus",
      "blur",
      "input",
      "change",
      "submit",
      "scroll",
      "resize",
      "touchstart",
      "touchend",
      "touchmove",
      "pointerdown",
      "pointerup",
      "pointermove"
    ];
    Object.keys(events).forEach((key) => {
      if (domEvents.includes(key.toLowerCase())) {
        console.warn(
          `ElementBuilder: "${key}" is a DOM event and should use .on('${key}', handler) instead of .withEvents(). withEvents() stores custom methods on model.events and does NOT attach DOM listeners.`
        );
      }
    });
    this.customEvents = { ...this.customEvents, ...events };
    return this;
  }
  // ============================================================================
  // MODIFIER METHODS
  // ============================================================================
  /**
   * Add a custom element modifier function
   * Useful for imperative DOM manipulation
   * @param modifier - Function that receives the element
   * @returns This builder for chaining
   */
  withModifier(modifier) {
    this.assertNotBuilt();
    this.modifiers.push(modifier);
    return this;
  }
  /**
   * Apply CSS animation to element
   * Phase 1: Generates animation shorthand property
   * Phase 2 (Future): Will support withKeyframes() for custom animations
   *
   * @param animation - Animation name (must be defined in CSS keyframes)
   * @param options - Animation timing options
   * @returns This builder for chaining
   *
   * @example
   * ```typescript
   * // Phase 1: Reference existing keyframes
   * button()
   *   .withAnimation('slideUnder', { duration: '300ms', delay: '100ms' })
   *   .build();
   * // Generates CSS: animation: slideUnder 300ms ease-in-out 100ms;
   * ```
   */
  withAnimation(animation, options) {
    this.assertNotBuilt();
    this.options.animation = { type: animation, ...options };
    const parts = [
      animation,
      options?.duration || "300ms",
      options?.easing || "ease-in-out",
      options?.delay || "0ms",
      options?.iterationCount?.toString() || "1",
      options?.direction || "normal",
      options?.fillMode || "none"
    ];
    this.withStyles(
      {
        element: {
          animation: parts.join(" ")
        }
      },
      1
    );
    return this;
  }
  /**
   * Get a reference to the element (useful for imperative operations)
   * The callback is called during build phase
   * @param callback - Function that receives the element
   * @returns This builder for chaining
   */
  ref(callback) {
    this.modifiers.push(callback);
    return this;
  }
  // ============================================================================
  // UTILITY METHODS
  // ============================================================================
  /**
   * Apply a function to this builder
   * Useful for extracting reusable builder patterns
   * @param fn - Function that receives this builder
   * @returns This builder for chaining
   */
  apply(fn) {
    return fn(this);
  }
  /**
   * Clone this builder (immutable branching)
   * Creates a new builder with a cloned element
   * @returns New builder instance
   */
  clone() {
    const cloned = new ElementBuilder(this.element.cloneNode(true), {
      ...this.options
    });
    cloned.classNames = new Set(this.classNames);
    cloned.styles = this.styles.clone();
    cloned.attributes = new Map(this.attributes);
    return cloned;
  }
  // ============================================================================
  // GETTER METHODS (Non-destructive)
  // ============================================================================
  /**
   * Get compiled styles without building the element
   * Useful when you only need the styles from a preset or builder
   * Does not mark the builder as built
   * @returns Compiled CSS string
   *
   * @example
   * ```typescript
   * const presetStyles = actions.primary().getStyles();
   *
   * const element = new ElementBuilder(myElement)
   *   .withStyles(presetStyles)
   *   .build();
   * ```
   */
  getStyles() {
    return this.styles.compile();
  }
  /**
   * Get accumulated class names
   * Returns array of all class names added to this builder
   * Does not mark the builder as built
   * @returns Array of class names
   *
   * @example
   * ```typescript
   * const builder = new ElementBuilder('div')
   *   .withClassName('container')
   *   .withClassName('active');
   *
   * const classes = builder.getClassNames(); // ['container', 'active']
   * ```
   */
  getClassNames() {
    return Array.from(this.classNames);
  }
  /**
   * Get the element with accumulated changes applied (classes, attributes)
   * Useful when you need the element reference before finalizing with .build()
   * Does not mark the builder as built
   * Does not apply children, modifiers, or event listeners
   * @returns The underlying HTML element with classes and attributes applied
   *
   * @example
   * ```typescript
   * const builder = new ElementBuilder('div')
   *   .withClassName('container')
   *   .withAttribute('data-test', 'true');
   *
   * const element = builder.getElement(); // Element has classes and attributes
   * // Builder can still be modified and built later
   * ```
   */
  getElement() {
    this.classNames.forEach((name) => this.element.classList.add(name));
    this.attributes.forEach((value, key) => {
      this.element.setAttribute(key, value);
    });
    return this.element;
  }
  // ============================================================================
  // TERMINAL METHODS (Build & Mount)
  // ============================================================================
  /**
   * Build the final element with all styles applied
   * This is the terminal operation that returns ElementModel
   * Can only be called once per builder instance
   * @returns ElementModel with element, styles, and lifecycle methods
   */
  build() {
    if (this.isBuilt) {
      const classNames = this.getClassNames();
      const classInfo = classNames.length > 0 ? ` (className: ${classNames.join(" ")})` : "";
      console.warn(
        `ElementBuilder.build() called multiple times${classInfo}. Returning cached result. This may indicate a logic error where the builder is being reused instead of creating a new one.`
      );
      return {
        element: this.element,
        styles: this.styles.compile(),
        update: (props) => this.update(props),
        destroy: () => this.destroy(),
        ...Object.keys(this.customEvents).length > 0 && {
          events: this.customEvents
        }
      };
    }
    this.isBuilt = true;
    this.classNames.forEach((name) => this.element.classList.add(name));
    this.attributes.forEach((value, key) => {
      this.element.setAttribute(key, value);
    });
    const childStyles = [];
    this.children.forEach((child) => {
      if (isElementBuilder(child)) {
        const built = child.build();
        this.element.appendChild(built.element);
        if (built.styles) {
          childStyles.push(built.styles);
        }
      } else if (isElementModel(child)) {
        this.element.appendChild(child.element);
        if (child.styles) {
          childStyles.push(child.styles);
        }
      } else if (child instanceof HTMLElement) {
        this.element.appendChild(child);
      } else if (typeof child === "string") {
        this.element.appendChild(document.createTextNode(child));
      }
    });
    this.modifiers.forEach((modifier) => {
      try {
        modifier(this.element);
      } catch (error) {
        console.error("Error applying modifier:", error);
      }
    });
    this.eventListeners.forEach((listeners, event) => {
      listeners.forEach(({ handler, options }) => {
        this.element.addEventListener(event, handler, options);
      });
    });
    const compiledStyles = this.styles.compile();
    const allStyles = [compiledStyles, ...childStyles].filter((s) => s && s.trim()).join("\n");
    this.lifecycle.setElement(this.element);
    const model = {
      element: this.element,
      styles: allStyles,
      update: (props) => this.update(props),
      destroy: () => this.destroy(),
      ...Object.keys(this.customEvents).length > 0 && {
        events: this.customEvents
      }
    };
    return model;
  }
  /**
   * Mount this element to a parent element
   * Automatically builds if not already built
   * @param parent - Parent element to append to
   * @returns This builder for chaining
   */
  mountTo(parent) {
    const result = this.isBuilt ? { element: this.element } : this.build();
    parent.appendChild(result.element);
    return this;
  }
  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================
  /**
   * Update the element with new props (for reactivity)
   * @param props - Properties to update
   */
  update(props) {
    Object.assign(this.options, props);
  }
  /**
   * Cleanup and destroy the element
   * Removes event listeners, cleans up resources, and removes from DOM
   */
  destroy() {
    this.lifecycle.cleanup();
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
  /**
   * Get current primary class name (for style generation)
   * @returns The first class name or null if no class names exist
   */
  getCurrentClassName() {
    const first = Array.from(this.classNames)[0];
    return first || null;
  }
  /**
   * Assert that build() hasn't been called yet
   * Throws error if builder is already built
   */
  assertNotBuilt() {
    if (this.isBuilt) {
      const classNames = this.getClassNames();
      const classInfo = classNames.length > 0 ? ` (className: ${classNames.join(" ")})` : "";
      throw new Error(
        `ElementBuilder: Cannot modify builder after build() has been called${classInfo}. Create a new builder instance instead of reusing a built one.`
      );
    }
  }
}
export {
  ElementBuilder
};
//# sourceMappingURL=ElementBuilder.js.map
